import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { PdfIcon } from 'components/SvgIcons';
import SideBar from 'components/SideBar/SideBar';
import Button from 'components/Button/Button';
import ProjectInfoPanel from 'components/ProjectInfoPanel/ProjectInfoPanel';
import RFPResponseStatusIndicator from 'components/RFPResponseStatusIndicator/RFPResponseStatusIndicator';
import EditableProjectInfoPanel from 'components/EditableProjectInfoPanel/EditableProjectInfoPanel';
import Tooltip from 'components/Tooltip/Tooltip';
import Loader from 'components/Loader/Loader';
import useFormattedDate from 'hooks/useFormattedDate';
import useUser from 'hooks/useUser';
import useICContact from 'hooks/useICContact/useICContact';

import { useDispatch, useSelector } from 'store';
import { updateRfpResponseName } from 'store/rfpResponse';
import RFPSubmitResponseStatus from 'types/RFPSubmitResponseStatus';
import { useSingleRfpResponseStats } from 'api';
import DaysLeft from './components/DaysLeft/DaysLeft';

import './KliparoSideBar.scss';

export interface KliparoSideBarProps {}

const KliparoSideBar: FC<KliparoSideBarProps> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id: rfpId, response_close_date } = useSelector((store) => store.rfp.data);
  const {
    id: rfpResponseId,
    project_name,
    rfp_response_status,
    rfp_submit_response_status,
    last_update,
    organization,
  } = useSelector((store) => store.rfpResponse.data);

  const { data: stats } = useSingleRfpResponseStats(rfpResponseId);
  const lastUpdate = useFormattedDate(last_update, 'dd-MMM-y');
  const { isReviewer } = useUser();

  const handleProjectNameChange = useCallback(
    async (newProjectName: string) => {
      await dispatch(updateRfpResponseName({ rfpId: rfpId, newProjectName })).unwrap();
    },
    [dispatch, rfpId],
  );

  const [contactEmail, contactName] = useICContact();

  return (
    <SideBar variant="kliparo" className="enkrateia-kliparo-side-bar" collapsible>
      <div className="side-bar-content">
        <ProjectInfoPanel
          variant="kliparo"
          label={t('pages.projectOverview.sidePanel.company')}
          value={organization.name}
        />
        <EditableProjectInfoPanel
          variant="kliparo"
          label={t('pages.projectOverview.sidePanel.programName')}
          value={project_name ?? t('pages.projectOverview.sidePanel.pleaseEnterProjectName')}
          className="span-2-column"
          disabled={rfp_submit_response_status === RFPSubmitResponseStatus.NO_SUBMISSIONS_REMAINING || isReviewer}
          onSubmit={handleProjectNameChange}
        />
        <ProjectInfoPanel
          label={t('pages.projectOverview.sidePanel.assessmentStatus')}
          value={<RFPResponseStatusIndicator status={rfp_response_status} />}
        />
        <div className="middle-panels">
          <ProjectInfoPanel
            withBorder
            label={t('pages.projectOverview.sidePanel.assessmentCompletion')}
            value={stats !== undefined ? `${Math.round(stats.percent_complete)}%` : <Loader />}
          />
          <ProjectInfoPanel
            withBorder
            label={t('pages.projectOverview.sidePanel.openTickets')}
            value={stats !== undefined ? stats.open_ticket_count : <Loader />}
          />
        </div>
        <ProjectInfoPanel
          variant="kliparo"
          label={t('pages.projectOverview.sidePanel.lastUpdated')}
          value={lastUpdate}
        />
        {Boolean(response_close_date) && <DaysLeft closeDate={response_close_date} />}
        <ProjectInfoPanel
          variant="kliparo"
          label={t('pages.projectOverview.sidePanel.ICContact')}
          value={<a href={`mailto:${contactEmail}`}>{contactName}</a>}
        />
        <Tooltip tooltip={t('pages.projectOverview.sidePanel.exportNotAvailableHelp')}>
          <Button variant="default" className="enkrateia-sidebar-button" disabled>
            <PdfIcon color="white" size={22} />
            {t('pages.projectOverview.sidePanel.exportResponses')}
          </Button>
        </Tooltip>
      </div>
    </SideBar>
  );
};

export default KliparoSideBar;
